import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  useTheme,
  LinearProgress,
  SxProps,
  Theme,
} from "@mui/material";
import { fetchApi } from "@tiny/utils";
import { formatNumber } from "@tiny/utils/formatUtils";

interface PlanTiersListProps {
  currentPrice?: number;
  showContactSupport?: boolean;
  sx?: SxProps<Theme>;
}

function PlanTiersList({
  currentPrice = 0,
  showContactSupport = true,
  sx = {},
}: PlanTiersListProps) {
  const theme = useTheme();
  const [tiers, setTiers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchApi(`/accountservice/subscriptions/tiers`)
      .then((res: any) => {
        if (res?.tiers) {
          setTiers(res.tiers.sort((a: any, b: any) => a.minCount - b.minCount));
        }
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Box
        sx={{
          bgcolor: "grey.20",
          borderRadius: 2,
          p: 2,
          textAlign: "center",
          ...sx,
        }}
      >
        <Typography variant="label2" sx={{ mb: 2 }}>
          Loading plans...
        </Typography>
        <LinearProgress sx={{ borderRadius: 1 }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: "grey.20",
        borderRadius: 2,
        p: 2,
        maxHeight: 250,
        overflowY: "auto",
        ...sx,
      }}
    >
      {tiers.map((tier: any) => {
        const isCurrentPlan = currentPrice === tier.price;
        return (
          <Box
            key={tier.name}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              py: 2,
              px: 1.5,
              mb: 0.5,
              borderRadius: 1,
              ...(isCurrentPlan && { bgcolor: "white" }),
            }}
          >
            <Typography
              variant="label2"
              sx={{
                fontWeight: isCurrentPlan ? 600 : 400,
                color: isCurrentPlan ? theme.palette.primary.main : "grey.90",
              }}
            >
              {formatNumber(tier.minCount, 0, true)} -{" "}
              {formatNumber(tier.maxCount, 0, true)}
            </Typography>
            <Typography
              variant="label2"
              sx={{
                fontWeight: 600,
                color: isCurrentPlan ? theme.palette.primary.main : "grey.90",
              }}
            >
              ${formatNumber(tier.price)}/mo
            </Typography>
          </Box>
        );
      })}

      {showContactSupport && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            py: 2,
            px: 1.5,
            borderRadius: 1,
          }}
        >
          <Typography
            variant="label2"
            sx={{ fontWeight: 600, color: "grey.90" }}
          >
            100M+
          </Typography>
          <Typography
            variant="label2"
            sx={{ fontWeight: 600, color: theme.palette.primary[100] }}
          >
            Contact Support
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default PlanTiersList;
